import { useEffect, useMemo, useState } from "react";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../../state/store";
import {
  addLocalizationToApp,
  setLocalizationToApp,
  setEditingLocale,
  setOriginalDefaultLocale
} from "../../../state/ducks";

import { appManageTypes, strCode } from "../../../consts";
import { t } from "../../../services";

import {
  CircleIconButton,
  FlexContainers,
  Paragraph2,
  SVG,
  Table,
  WrapperContainer
} from "../../../components";
import { LocalisationTabTableColumns } from "./LocalisationTabTableColumns";
import {
  selectEditingLocale,
  selectOriginalDefaultLocale,
  selectSelectedAppLocalizations
} from "../../../state/selectors";
import { useSelector } from "react-redux";

const LocalisationTabTable = ({ localizations }) => {
  const manageType = useSelector((store) => store.apps.manageType);

  const [editingLocalization, setEditingLocalization] = useState(null);
  const [originalDefaultLocalization, setOriginalDefaultLocalization] = useState(
    localizations.find((localization) => localization.isDefault)
  );
  const [localizationToRefetch, setLocalizationToRefetch] = useState(null);

  useEffect(() => {
    dispatch(setEditingLocale(editingLocalization));
  }, [editingLocalization]);

  useEffect(() => {
    dispatch(setOriginalDefaultLocale(originalDefaultLocalization));
  }, [originalDefaultLocalization]);

  const columns = useMemo(() => LocalisationTabTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data: localizations
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const addLocalization = () => {
    const newLocalization = {
      path: "",
      language: "",
      name: "",
      isDefault: localizations.length === 0,
      isNew: true
    };

    dispatch(addLocalizationToApp(newLocalization));
    setEditingLocalization(newLocalization);
  };

  useEffect(() => {
    return () => {
      const appLocalizations = selectSelectedAppLocalizations()(getState());
      const edited = selectEditingLocale()(getState());
      const defaultLocale = selectOriginalDefaultLocale()(getState());

      const savedLocalization = appLocalizations
        .filter((localization) => localization.language && localization.name && localization.path)
        .map((localization) => {
          return {
            ...localization,
            isDefault:
              manageType === appManageTypes.EDIT
                ? edited
                  ? localization.name === defaultLocale.name
                  : localization.isDefault
                : localization.isDefault
          };
        });

      dispatch(setLocalizationToApp(savedLocalization));
    };
  }, []);

  return (
    <WrapperContainer mt={24} width="100%">
      <Table
        tableInstance={tableInstance}
        component={
          <WrapperContainer
            Container={FlexContainers.FlexColumnCenter}
            gap={16}
            height={localizations.length ? 60 : 82}
            mt={localizations.length ? 0 : 24}>
            {!localizations.length && <Paragraph2>{t(strCode.NO_TRANSLATION_YET)}</Paragraph2>}

            <CircleIconButton
              onClick={addLocalization}
              disabled={editingLocalization}
              tooltipLabel={t(strCode.NEW_BTN)}>
              <SVG.PlusIcon />
            </CircleIconButton>
          </WrapperContainer>
        }
        componentHeight={64}
        maxTableHeight="186px"
        callbackFn1={() => editingLocalization}
        callbackFn2={setEditingLocalization}
        callbackFn3={() => originalDefaultLocalization}
        callbackFn4={setOriginalDefaultLocalization}
        callbackFn5={() => localizationToRefetch}
        callbackFn6={setLocalizationToRefetch}
        hasPagination
        showTableBorder
        disableScroll
      />
    </WrapperContainer>
  );
};

LocalisationTabTable.propTypes = {
  localizations: PropTypes.arrayOf(
    PropTypes.shape({
      language: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      isDefault: PropTypes.bool.isRequired
    })
  ).isRequired
};

export default LocalisationTabTable;
